const ASSERTIVE_TYPE_CLASSES: ReadonlyArray<string> = ['ajs-error', 'ajs-warning'];

// Tracks toasts that this module moved focus to, so the post-dismissal redirect
// only fires for toasts we own and not for alertify calls from other components.
const ownedToasts = new WeakSet<HTMLElement>();

// Tracks the Contact Us section that triggered the current submission so
// focus returns to the right instance on multi-stack pages.
// Set by the inline submit handler in stack_contact_us.php before form dispatch.
let activeContactSection: HTMLElement | null = null;

const setActiveContactSection = (section: HTMLElement | null): void => {
    activeContactSection = section;
};

// Expose setter for the PHP inline submit handler to call before form dispatch.
declare global {
    interface Window {
        SSContactUsA11y?: { setActiveContactSection: typeof setActiveContactSection };
    }
}
window.SSContactUsA11y = { setActiveContactSection };

const redirectFocusAfterDismissal = (isError: boolean): void => {
    if (isError) {
        // Prefer the section that triggered this submission; fall back to the first
        // Contact Us section in DOM order (correct for single-instance pages).
        const section =
            activeContactSection ??
            document.querySelector<HTMLElement>('section[data-comp="contact-us"]');
        if (section) {
            const prior = section.getAttribute('tabindex');
            section.setAttribute('tabindex', '-1');
            section.focus();
            section.addEventListener('blur', () => {
                if (prior === null) {
                    section.removeAttribute('tabindex');
                } else {
                    section.setAttribute('tabindex', prior);
                }
            }, { once: true });
        }
    } else {
        // Success — put focus at the top of the page landmark, then <h1> as fallback.
        // Selector order matters: [role="main"]/main is the canonical page region;
        // h1 is a last resort because it may sit inside a banner/nav rather than main.
        const target = document.querySelector<HTMLElement>('[role="main"], main, h1');
        if (target) {
            const prior = target.getAttribute('tabindex');
            if (prior === null) target.setAttribute('tabindex', '-1');
            target.focus();
            target.addEventListener('blur', () => {
                if (prior === null) {
                    target.removeAttribute('tabindex');
                } else {
                    target.setAttribute('tabindex', prior);
                }
            }, { once: true });
        }
    }
};

// Annotate an alertify message element and move keyboard focus to it.
//
// WHY focus the toast instead of relying on aria-live alone:
//   VoiceOver (iOS 17/Safari) and NVDA (Chrome, post-submit full-page reload) were
//   silently dropping aria-live announcements when the toast node was inserted after
//   DOMContentLoaded — the live region mutation happened too late in the parse/render
//   cycle to be picked up. Focusing the element forces the AT to read it immediately,
//   regardless of live-region timing. Annotate-only was validated first and failed on
//   both of those AT/browser pairs.
//
// After the toast auto-dismisses, focus is redirected to an appropriate landmark so
// keyboard users are not left on a detached node.
const announce = (messageEl: HTMLElement): void => {
    if (messageEl.dataset.ssA11yAnnotated === '1') return;
    // Only handle toasts stamped by Contact Us PHP; ignore toasts from other stacks.
    if (messageEl.dataset.ssContactToast !== '1') return;
    messageEl.dataset.ssA11yAnnotated = '1';

    const isAssertive = ASSERTIVE_TYPE_CLASSES.some((cls) => messageEl.classList.contains(cls));
    messageEl.setAttribute('role', isAssertive ? 'alert' : 'status');
    messageEl.setAttribute('aria-atomic', 'true');
    messageEl.setAttribute('tabindex', '-1');

    // 50 ms: yield to alertify's own DOM insertion/animation sequence so the element
    // is fully painted before we focus it (avoids a visible flash on some browsers).
    setTimeout(() => {
        messageEl.focus();
        ownedToasts.add(messageEl);
    }, 50);
};

const handleRemovedNode = (node: Node): void => {
    if (!(node instanceof HTMLElement)) return;

    // Only redirect focus for toasts this module focused; ignore alertify calls
    // from unrelated components that happen to share the same page.
    if (node.classList.contains('ajs-message') && ownedToasts.has(node)) {
        ownedToasts.delete(node);
        const isAssertive = ASSERTIVE_TYPE_CLASSES.some((cls) => node.classList.contains(cls));
        // 1200 ms: empirically lets NVDA finish the current utterance before focus
        // moves away from the dismissed toast (tuned against NVDA 2023.1 on Chrome).
        setTimeout(() => redirectFocusAfterDismissal(isAssertive), 1200);
    }
};

const handleAddedNode = (node: Node): void => {
    if (!(node instanceof HTMLElement)) return;
    if (node.classList.contains('ajs-message')) {
        announce(node);
    } else if (node.classList.contains('alertify-notifier')) {
        node.querySelectorAll<HTMLElement>('.ajs-message').forEach(announce);
    }
};

const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
        mutation.addedNodes.forEach(handleAddedNode);
        mutation.removedNodes.forEach(handleRemovedNode);
    });
});

observer.observe(document.body, { childList: true, subtree: true });

// Initial scan: catches toasts already in the DOM when this deferred ESM runs.
// Post-submit reload — alertify fires in $(document).ready which flushes on
// DOMContentLoaded, before deferred modules execute. We either scan immediately
// (readyState is already 'complete') or wait for the event.
const scanExisting = (): void => {
    document.querySelectorAll<HTMLElement>('.ajs-message').forEach(announce);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scanExisting, { once: true });
} else {
    scanExisting();
}
